import { useState, useEffect } from 'react';
import { useForm, Controller, SubmitHandler } from 'react-hook-form';
import { useLocation } from 'react-router-dom';
import { isAxiosError } from 'axios';
import axios from '../../api/axios';
import MessageBox from '../MessageBox/MessageBox';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import logo from '../../assets/yapa-logo.png';
import './ResetPasswordPage.css';

interface IFormInput {
    newPassword: string;
    confirm: string;
}

const RESET_URL = '/auth/update';

const PASS_REGEX = /^(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])(?=.*[!@#$%]).{8,24}$/;

const RESET_BUTTON_TITLE = 'Reset Password';
const CONNECTION_ERROR_MESSAGE = 'Nie można nawiązać połączenia z serwerem!';
const INVALID_CREDENTIALS_MESSAGE = 'Problem z tokenem. Wejdź jeszcze raz na stronę przez link w mailu!';
const UNKNOWN_ERROR_MESSAGE = 'Nieznany błąd!';
const SUCCESS = 'Zmiana hasła przebiegła pomyślnie!';

const ResetPasswordPage = () => {
    const location = useLocation();
    const [tokenFromUrl, setTokenFromUrl] = useState<string>('');
    const [successMsg, setSuccessMsg] = useState<string>('');
    const [errorMsg, setErrorMsg] = useState<string>('');
    const [open, setOpen] = useState(false);
    const [passwordChanged, setPasswordChanged] = useState<boolean>(false);

    useEffect(() => {
        const searchParams = new URLSearchParams(location.search);
        const token = searchParams.get('token');
        if (token) {
            setTokenFromUrl(token);
        }
    }, [location.search]);

    const {
        control,
        handleSubmit,
        getValues,
        formState: { errors },
    } = useForm<IFormInput>({
        defaultValues: {
            newPassword: '',
            confirm: '',
        },
    });

    const onSubmit: SubmitHandler<IFormInput> = async (data) => {
        const { newPassword } = data;
        const token = tokenFromUrl;

        try {
            await axios.patch(
                RESET_URL,
                JSON.stringify({ token, newPassword }),
                {
                    headers: { 'Content-Type': 'application/json' },
                    withCredentials: true,
                }
            );
            setSuccessMsg(SUCCESS);
            setPasswordChanged(true);

            window.location.href = '/obsuwka';

        } catch (error) {
            if (isAxiosError(error)) {
                if (!error?.response) {
                    setErrorMsg(CONNECTION_ERROR_MESSAGE);
                } else if (error.response?.status) {
                    setErrorMsg(INVALID_CREDENTIALS_MESSAGE);
                }
            } else {
                setErrorMsg(UNKNOWN_ERROR_MESSAGE);
            }
        }
    };

    return (
        <div className="login-container">
            <img src={logo} width={250} alt="Logo YAPA" />
            <div className="login-content">
                <span>Zmień hasło</span>
                {!passwordChanged && (
                    <form className="login-form" onSubmit={handleSubmit(onSubmit)}>
                        <Controller
                            name="newPassword"
                            control={control}
                            rules={{
                                required: true,
                                pattern: PASS_REGEX,
                            }}
                            render={({ field }) => (
                                <Tooltip
                                    title={
                                        <p style={{ fontSize: '0.8rem' }}>
                                            - Hasło musi mieć od 8 do 24 znaków, w tym:
                                            <br />
                                            - dużą i małą literę
                                            <br />
                                            - cyfrę
                                            <br />- znak specjalny ( ! @ # $ % )
                                        </p>
                                    }
                                    placement="right"
                                    arrow
                                    disableHoverListener
                                >
                                    <TextField
                                        type="password"
                                        label="Hasło"
                                        {...field}
                                        error={errors.newPassword ? true : false}
                                        helperText={errors.newPassword ? 'Niepoprawne hasło' : ''}
                                    />
                                </Tooltip>
                            )}
                        />

                        <Controller
                            name="confirm"
                            control={control}
                            rules={{
                                required: true,
                                validate: (confirm) => confirm === getValues('newPassword'),
                            }}
                            render={({ field }) => (
                                <TextField
                                    type="password"
                                    label="Potwierdź Hasło"
                                    {...field}
                                    error={errors.confirm ? true : false}
                                    helperText={errors.confirm ? 'Hasła nie są takie same' : ''}
                                />
                            )}
                        />

                        <Button
                            variant="contained"
                            type="submit"
                            size="large"
                            sx={{ marginTop: '20px' }}
                        >
                            {RESET_BUTTON_TITLE}
                        </Button>
                    </form>
                )}
                {passwordChanged && (
                    <div className="success-msg">
                        {successMsg}
                    </div>
                )}
            </div>
            <MessageBox
                open={open}
                setOpen={() => setOpen(prevOpen => !prevOpen)}
                message={errorMsg}
                error={errorMsg !== SUCCESS}
            />
        </div>
    );
};

export default ResetPasswordPage;
